import { createContext, useContext, useEffect, useState } from "react";
import axios, { AxiosInstance } from "axios";
import { ApiErrorCode } from "helpers/ApiErrorCode";
import { normalizeRole, Role } from "helpers/roles";
import Cookies from "js-cookie";
import { useNotification } from "providers/Notification";

export const LOGIN_PATH = "/login";

//IsUnauthorizedError reports whether an error is the API's "not authorized"
//response. Callers rarely need this: the instance returned by GetAxios already
//logs the user out and redirects. It is exported for the few places that must
//react to an expired session themselves.
export function IsUnauthorizedError(err: unknown): boolean {
    return axios.isAxiosError(err)
        && !!err.response
        && err.response.data?.code === ApiErrorCode.ApiErrorUnauthorized;
}

// NS-753
//IsForbiddenError reports whether an error is an HTTP 403: the session is
//valid, but its role does not allow the request.
export function IsForbiddenError(err: unknown): boolean {
    return axios.isAxiosError(err)
        && !!err.response
        && err.response.status === 403;
}

//WRITE_METHODS are the request methods that change data. A 403 on a read is
//left to the page: some pages expect it for a limited role and handle it.
const WRITE_METHODS = ["post", "put", "patch", "delete"];

export enum LoginErrorEnum {
    LoginUnknownError = 0,
    LoginInvalidCredentials,
}
export interface AuthContextData {
    LoggedIn: boolean
    ConnectionOk: boolean
    DatabaseOk: boolean
    ActivityDatabaseOk: boolean
    ServerMode: ServerModeEnum
    Login: (username: string, password: string) => Promise<void>
    Logout: () => Promise<void>
    //GetAxios returns an axios instance with the correct authentication headers.
    //Will throw if not logged in.
    GetToken: () => string | undefined
    GetAxios: () => AxiosInstance | undefined
    //GetLoginDetail gets login information. Returns undefined if not logged in
    GetLoginDetail: () => Promise<LoginDetail | undefined>
    //CurrentUser is the result of the last GetLoginDetail call. It is
    //undefined until then, and after log-out
    CurrentUser: LoginDetail | undefined
    FetchVersion: () => Promise<VersionInfo | undefined>
}

export enum ServerModeEnum {
    UNKNOWN = "",
    ALL = "all",
    WEB = "web",
    ALERT = "alert",
    ICAP = "icap"
}

export interface CriticalIssue {
    description: string
}

export interface LoginDetail {
    username: string
    databaseType: string
    role: Role
}

export interface VersionInfo {
    version: string
    buildDate: string
}

const LoginContext = createContext<AuthContextData>({} as AuthContextData);

interface LoginProviderProps {
    children: React.ReactChild | React.ReactChild[]
}

function LoginProvider(props: LoginProviderProps) {
    let token = localStorage.getItem("auth_token") || "";
    if (!token) {
        //if there's no token, check if the server sent it through cookies
        token = Cookies.get("SessionToken") || "";

        if (token !== "") {
            Cookies.remove("SessionToken");
            localStorage.setItem("auth_token", token);
            console.info("got auth token from cookie");
        }
    }

    const [LoggedIn, setLoggedIn] = useState(token ? true : false);
    const [DatabaseOk, setDatabaseOk] = useState(true);
    const [ActivityDatabaseOk, setActivityDatabaseOk] = useState(true);
    const [ConnectionOk, setConnectionOk] = useState(true);
    const [ServerMode, setServerMode] = useState(ServerModeEnum.UNKNOWN);
    // NS-753
    const [CurrentUser, setCurrentUser] = useState<LoginDetail>();
    const notification = useNotification();

    const Login = async (username: string, password: string) => {
        const formData = new URLSearchParams();

        formData.append("grant_type", "client_credentials");
        formData.append("client_id", username);
        formData.append("client_secret", password);


        try {
            const resp = await axios.post("/api/auth", formData);
            localStorage.setItem("auth_token", resp.data.access_token);
            setLoggedIn(true);
        } catch (err) {
            if (axios.isAxiosError(err) && err.response) {
                //the request was made and the server responded with a non-200 status code
                const body = err.response.data;

                if (body.code === ApiErrorCode.ApiErrorInvalidCredentials) {
                    throw new LoginError(
                        LoginErrorEnum.LoginInvalidCredentials,
                        "Incorrect username or password. Please try again.",
                    );
                } else {
                    console.error(err);
                    throw new LoginError(
                        LoginErrorEnum.LoginUnknownError,
                        "Log-in failed",
                    );
                }
            }
            throw err;
        }


    };

    const GetToken = () => {
        return token;
    };

    //handleUnauthorized tears down the local session and sends the user back to
    //the login page. The provider is mounted outside the router, so navigation
    //goes through the browser rather than react-router.
    const handleUnauthorized = () => {
        localStorage.removeItem("auth_token");
        setLoggedIn(false);
        setCurrentUser(undefined);

        if (window.location.pathname !== LOGIN_PATH) {
            window.location.assign(LOGIN_PATH);
        }
    };

    //newAuthenticatedClient builds an axios instance carrying the session token.
    //Provider-internal callers pass intercept=false: they handle an expired
    //session themselves and must not trigger the redirect.
    const newAuthenticatedClient = (intercept: boolean) => {
        const client = axios.create({
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (intercept) {
            client.interceptors.response.use(undefined, (err) => {
                if (IsUnauthorizedError(err)) {
                    handleUnauthorized();
                } else if (IsForbiddenError(err) && WRITE_METHODS.includes(err.config?.method?.toLowerCase() || "")) {
                    // NS-753
                    notification.Display({
                        type: "error",
                        title: "Permission denied",
                        message: "Your role does not allow this action",
                    });
                }

                return Promise.reject(err);
            });
        }

        return client;
    };

    const GetAxios = () => {
        if (!LoggedIn) {
            return;
        }

        return newAuthenticatedClient(true);
    };

    const Logout = async () => {
        try {
            if (!LoggedIn) {
                console.error("not logged in");
                return;
            }

            //fire-and-forget, and deliberately not intercepted: an expired token
            //here just means the session we are dropping is already gone
            void newAuthenticatedClient(false).post("/api/auth/logout").catch((err) => {
                //log the status and nothing else: an AxiosError carries the
                //request config, whose headers include the session's bearer token
                const status = axios.isAxiosError(err) ? err.response?.status : undefined;
                console.error(`log-out request failed${status === undefined ? "" : ` (status ${status})`}`);
            });
            localStorage.removeItem("auth_token");
            setLoggedIn(false);
            setCurrentUser(undefined);
        } catch(err) {
            throw new LoginError(
                LoginErrorEnum.LoginUnknownError,
                "Log-out failed",
            );
        }
    };

    const GetLoginDetail = async () => {
        try {
            if (!LoggedIn) {
                return; //not logged in
            }

            //opts out of the interceptor: the caller decides what to do when
            //this comes back unauthorized
            const res = await newAuthenticatedClient(false).get("/api/auth");

            const detail: LoginDetail = {
                username: res.data.username,
                databaseType: res.data.databaseType,
                // NS-753
                role: normalizeRole(res.data.role),
            };
            setCurrentUser(detail);

            return detail;
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                localStorage.removeItem("auth_token");
                setLoggedIn(false);
                setCurrentUser(undefined);
                return;
            }
            console.error(err); //TODO: report error to the user somehow
            return;
        }

    };

    const FetchVersion = async (): Promise<VersionInfo | undefined> => {
        const ax = GetAxios();
        if (!ax) {
            return;
        }

        const respVersion = await ax.get("/api/config/version");

        return {
            version: respVersion.data.version,
            buildDate: respVersion.data.buildDate,
        };
    };

    useEffect(() => {
        const item = {} as { timeout?: NodeJS.Timeout };

        const loop = async () => {
            try {
                const response = await axios.get("/api/serverstatus");

                setDatabaseOk(response.data.databaseStatus === "connected");
                setActivityDatabaseOk(response.data.activityDatabaseStatus === "connected");
                setConnectionOk(true);
                setServerMode(response.data.mode);
            } catch (e: any) {
                console.error(e);
                if (axios.isAxiosError(e) && e.response) {
                    if(e.response) {
                        setDatabaseOk(false);
                        setConnectionOk(true);
                    } else {
                        setDatabaseOk(false);
                        setConnectionOk(false);
                    }
                } else {
                    setDatabaseOk(false);
                    setConnectionOk(false);
                }
            }
            item.timeout = setTimeout(loop, 5000);
        };

        loop();

        return () => {
            if (item.timeout) {
                clearTimeout(item.timeout);
            }
        };

    }, []);

    return (
        <LoginContext.Provider value={{ ServerMode, LoggedIn, DatabaseOk, ActivityDatabaseOk, ConnectionOk, Login, Logout, GetAxios, GetToken, GetLoginDetail, CurrentUser, FetchVersion }}>
            {props.children}
        </LoginContext.Provider>
    );
}

export function useLogin() {
    return useContext(LoginContext);
}

export class LoginError extends Error {
    Type: LoginErrorEnum;
    constructor(e: LoginErrorEnum, message: string) {
        super(message);
        Object.setPrototypeOf(this, LoginError.prototype);
        this.Type = e;
    }
}



export default LoginProvider;
